import { YardTaskStatus } from '@nova/core';

import { areTagsDifferent, isCheckTask, isMoveTask } from '@satellite/helpers/yardTaskUtils';

import type { CheckTaskFormData, MoveTaskFormData } from '@nova/core';

export interface DisabledFieldsContext {
  taskStatus: string;
  taskType: 'move' | 'check';
}

/**
 * Creates a disabled fields configuration based on task status, type, and user permissions
 */
export function createDisabledFieldsConfig(context: DisabledFieldsContext) {
  const { taskStatus } = context;

  switch (taskStatus) {
    case YardTaskStatus.COMPLETED:
    case YardTaskStatus.CANCELLED:
      return {
        description: false,
        attachments: false,
        assignee: true,
        dueByDate: true,
        dueByTime: true,
        tags: true
      };

    case YardTaskStatus.IN_PROGRESS:
      return {
        description: false,
        attachments: false,
        assignee: true,
        dueByDate: false,
        dueByTime: false,
        tags: false
      };

    case YardTaskStatus.CREATED:
    default:
      return {
        description: false,
        attachments: false,
        assignee: false,
        dueByDate: false,
        dueByTime: false,
        tags: false
      };
  }
}

/**
 * Creates a disabled fields configuration specifically for move tasks
 */
export function createMoveTaskDisabledFields(context: DisabledFieldsContext) {
  const baseConfig = createDisabledFieldsConfig(context);

  switch (context.taskStatus) {
    case YardTaskStatus.COMPLETED:
    case YardTaskStatus.CANCELLED:
    case YardTaskStatus.IN_PROGRESS:
      return {
        ...baseConfig,
        asset: true,
        originSpot: true,
        destinationSpot: true
      };

    case YardTaskStatus.CREATED:
    default:
      return {
        ...baseConfig,
        asset: false,
        originSpot: false,
        destinationSpot: false
      };
  }
}

/**
 * Creates a disabled fields configuration specifically for check tasks
 */
export function createCheckTaskDisabledFields(context: DisabledFieldsContext) {
  const baseConfig = createDisabledFieldsConfig(context);

  switch (context.taskStatus) {
    case YardTaskStatus.COMPLETED:
    case YardTaskStatus.CANCELLED:
      return {
        ...baseConfig,
        subject: true,
        spot: true,
        area: true,
        asset: true,
        category: false,
        checklist: true
      };

    case YardTaskStatus.IN_PROGRESS:
      return {
        ...baseConfig,
        subject: true,
        spot: true,
        area: true,
        asset: true,
        category: false,
        checklist: false
      };

    case YardTaskStatus.CREATED:
    default:
      return {
        ...baseConfig,
        subject: false,
        spot: false,
        area: false,
        asset: false,
        category: false,
        checklist: false
      };
  }
}

export function createTaskDisabledFields(context: DisabledFieldsContext) {
  if (context.taskType === 'move') {
    return createMoveTaskDisabledFields(context);
  } else if (context.taskType === 'check') {
    return createCheckTaskDisabledFields(context);
  }

  return {};
}

export function hasCheckTaskChanged(
  currentTaskData: CheckTaskFormData,
  fetchedTaskData: CheckTaskFormData
) {
  const currChecklist = Array.isArray(currentTaskData.checklist) ? currentTaskData.checklist : [];
  const prevChecklist = Array.isArray(fetchedTaskData.checklist) ? fetchedTaskData.checklist : [];
  const checklistChanges =
    currChecklist.length !== prevChecklist.length ||
    currChecklist.some((item, index) => item.description !== prevChecklist[index]?.description);

  const dueByTimeChanges =
    currentTaskData.dueByTime?.amPm !== fetchedTaskData.dueByTime?.amPm ||
    currentTaskData.dueByTime?.time !== fetchedTaskData.dueByTime?.time;
  const dueByDateChanges = currentTaskData.dueByDate !== fetchedTaskData.dueByDate;
  const flagsChanges = areTagsDifferent(currentTaskData.tags, fetchedTaskData.tags);
  const assigneeChanges = currentTaskData.assignee !== fetchedTaskData.assignee;
  const spotChanges = currentTaskData.spot !== fetchedTaskData.spot;
  const areaChanges = currentTaskData.area !== fetchedTaskData.area;
  const assetChanges = currentTaskData.asset !== fetchedTaskData.asset;
  const subjectChanges = currentTaskData.subject !== fetchedTaskData.subject;
  const categoryChanges = currentTaskData.category !== fetchedTaskData.category;
  const descriptionChanges = currentTaskData.description !== fetchedTaskData.description;

  return (
    checklistChanges ||
    dueByTimeChanges ||
    dueByDateChanges ||
    flagsChanges ||
    assigneeChanges ||
    spotChanges ||
    areaChanges ||
    assetChanges ||
    subjectChanges ||
    categoryChanges ||
    descriptionChanges
  );
}

export function hasMoveTaskChanged(
  currentTaskData: MoveTaskFormData,
  fetchedTaskData: MoveTaskFormData
) {
  const assetChanges = currentTaskData.asset !== fetchedTaskData.asset;
  const originSpotChanges = currentTaskData.originSpot !== fetchedTaskData.originSpot;
  const destinationSpotChanges =
    currentTaskData.destinationSpot !== fetchedTaskData.destinationSpot;
  const descriptionChanges = currentTaskData.description !== fetchedTaskData.description;
  const dueByDateChanges = currentTaskData.dueByDate !== fetchedTaskData.dueByDate;
  const dueByTimeChanges =
    currentTaskData.dueByTime?.amPm !== fetchedTaskData.dueByTime?.amPm ||
    currentTaskData.dueByTime?.time !== fetchedTaskData.dueByTime?.time;
  const flagsChanges = areTagsDifferent(currentTaskData.tags, fetchedTaskData.tags);
  const assigneeChanges = currentTaskData.assignee !== fetchedTaskData.assignee;

  return (
    assetChanges ||
    originSpotChanges ||
    destinationSpotChanges ||
    dueByDateChanges ||
    dueByTimeChanges ||
    flagsChanges ||
    assigneeChanges ||
    descriptionChanges
  );
}

export function hasTaskChanged(
  currentTaskData: CheckTaskFormData | MoveTaskFormData,
  fetchedTaskData: CheckTaskFormData | MoveTaskFormData
) {
  if (isCheckTask(currentTaskData) && isCheckTask(fetchedTaskData)) {
    return hasCheckTaskChanged(currentTaskData, fetchedTaskData);
  } else if (isMoveTask(currentTaskData) && isMoveTask(fetchedTaskData)) {
    return hasMoveTaskChanged(currentTaskData, fetchedTaskData);
  }

  return false;
}
